/** Blender mini-chart history and job pairs for the fleet hub. */

export const HISTORY_WINDOW_MS = 60 * 60 * 1000

/** jobPairs[] → { [licenseId]: { pumpJobId, blenderJobId, mpcJobId } } (camelCase or PascalCase payload) */
export const normalizeJobPairs = (payload) => {
  const raw = payload?.jobPairs ?? payload?.JobPairs
  const pairs = Array.isArray(raw) ? raw : []
  return pairs.reduce((acc, pair) => {
    const licenseId = pair?.licenseId ?? pair?.LicenseId
    if (!licenseId) return acc
    acc[licenseId] = {
      pumpJobId: pair.pumpJobId ?? pair.PumpJobId ?? null,
      blenderJobId: pair.blenderJobId ?? pair.BlenderJobId ?? null,
      mpcJobId: pair.mpcJobId ?? pair.MpcJobId ?? null,
    }
    return acc
  }, {})
}

const trimToWindow = (points) => {
  if (!points.length) return points
  const cutoff = points[points.length - 1].timestamp - HISTORY_WINDOW_MS
  return points[0].timestamp >= cutoff ? points : points.filter((p) => p.timestamp >= cutoff)
}

const appendPoints = (existing, incoming) => {
  if (!existing.length) return incoming
  const lastTs = existing[existing.length - 1].timestamp
  // Guard against overlapping re-sends: only take points newer than what we already have
  const fresh = incoming[0]?.timestamp > lastTs ? incoming : incoming.filter((p) => p.timestamp > lastTs)
  return fresh.length ? existing.concat(fresh) : existing
}

/**
 * Apply one FleetDataResponse to the history store, kept outside the unit objects:
 * { [unitUId]: { [fieldName]: { fieldName, unit, isCustom, channelId, channelName, data: Point[] } } }
 *
 * - isHistoryReset (message) → clear everything
 * - history[].isReset (series) → replace that series (first send, or its channel was overridden / reverted)
 * - otherwise append; history null / missing series → no new points, keep as is
 * - trim to 1 h relative to the series' last point
 * - drop series whose field left `fields`, and units that left `units`
 */
export const mergeFleetHistory = (prev, payload) => {
  const units = Array.isArray(payload?.units) ? payload.units : []
  const base = payload?.isHistoryReset ? {} : (prev || {})
  const next = {}

  units.forEach((unit) => {
    if (!unit?.unitUId) return
    const fields = Array.isArray(unit.fields) ? unit.fields : []
    const liveFields = new Set(fields.filter((f) => f?.fieldName).map((f) => f.fieldName))

    const unitSeries = {}
    const prevSeries = base[unit.unitUId] || {}
    Object.keys(prevSeries).forEach((fieldName) => {
      if (liveFields.has(fieldName)) unitSeries[fieldName] = prevSeries[fieldName]
    })

    const history = Array.isArray(unit.history) ? unit.history : []
    history.forEach((h) => {
      if (!h?.fieldName) return
      const incoming = (Array.isArray(h.data) ? h.data : [])
        .filter((p) => p && p.timestamp != null && p.value != null)
      const existing = h.isReset ? [] : (unitSeries[h.fieldName]?.data || [])
      unitSeries[h.fieldName] = {
        fieldName: h.fieldName,
        unit: h.unit ?? null,
        isCustom: Boolean(h.isCustom),
        channelId: h.channelId ?? null,
        channelName: h.channelName ?? null,
        data: trimToWindow(appendPoints(existing, incoming)),
      }
    })

    if (Object.keys(unitSeries).length) next[unit.unitUId] = unitSeries
  })

  return next
}

export const fieldOverrideKey = (unitUId, fieldName) => `${unitUId}::${fieldName}`
